import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useKACState } from "./hooks/useKACState";
import Toast from "./components/Toast";
import PlayerModal from "./components/PlayerModal";
import NewGroupModal from "./components/NewGroupModal";
import TutorialModal from "./components/TutorialModal";
import SecondClinicModal from "./components/SecondClinicModal";
import TermsModal from "./components/TermsModal";
import LoginScreen from "./components/LoginScreen";
import ChangePasswordModal from "./components/ChangePasswordModal";
import { KacBadge, KacWordmark } from "./components/KacMark";
import MemberScheduleView from "./views/MemberScheduleView";
import RosterView from "./views/RosterView";
import HouseholdsView from "./views/HouseholdsView";
import GroupsView from "./views/GroupsView";
import WeeklyGridView from "./views/WeeklyGridView";
import FindTimeView from "./views/FindTimeView";
import FindPlayerView from "./views/FindPlayerView";
import FindOpeningsView from "./views/FindOpeningsView";
import CoachesView from "./views/CoachesView";
import StatsView from "./views/StatsView";
import IssuesView from "./views/IssuesView";
import ApplicationsView from "./views/ApplicationsView";
import EmailView from "./views/EmailView";
import JoinView from "./views/JoinView";
import AccountsView from "./views/AccountsView";
import ActivityView from "./views/ActivityView";
import ApprovalsView from "./views/ApprovalsView";
import { fetchApplications } from "./utils/applications";
import { fetchSession, logout, onSessionExpired, fetchAccounts, fetchApprovals, requestApproval, decideApproval } from "./utils/auth";
import { pendingOverlay, summarizeRequest, approvalBlocker, applyApproval } from "./utils/approvals";
import { getWarnings } from "./utils/validation";
import { getScheduleIssues, countActionableIssues } from "./utils/issues";
import { getWaitlist, hasSpace } from "./utils/waitlist";
import { countNeedingPlacement } from "./utils/placement";
import { otherClinicsForPlayer } from "./utils/membership";

const ListIcon = (
  <svg viewBox="0 0 20 20" fill="none" className="w-4 h-4" aria-hidden="true">
    <path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);
const GridIcon = (
  <svg viewBox="0 0 20 20" fill="none" className="w-4 h-4" aria-hidden="true">
    <rect x="3" y="3" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.8" />
    <rect x="11" y="3" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.8" />
    <rect x="3" y="11" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.8" />
    <rect x="11" y="11" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

// Side-rail navigation. Every destination is one row — the old Schedule and
// Find tabs' segmented sub-toggles are just nested rows here, so there's a
// single place to look for "where do I go". Stats / Issues / Email sit
// ungrouped at the bottom: they're things you do, not a category to browse.
// `tab` + `sub` map a row onto the view state AppShell already keeps.
const NAV = [
  {
    label: "Schedule",
    items: [
      { key: "schedule-list", label: "List", tab: "Schedule", sub: "List", icon: ListIcon },
      { key: "schedule-grid", label: "Grid", tab: "Schedule", sub: "Grid", icon: GridIcon },
    ],
  },
  {
    label: "Members",
    items: [
      { key: "roster", label: "Roster", tab: "Roster" },
      { key: "households", label: "Households", tab: "Households" },
      { key: "signups", label: "Sign-ups", tab: "Sign-ups", badge: "signups" },
      { key: "coaches", label: "Coaches", tab: "Coaches" },
    ],
  },
  {
    label: "Planning",
    items: [
      { key: "find-time", label: "Find by time", tab: "Find", sub: "Time" },
      { key: "find-player", label: "Find by player", tab: "Find", sub: "Player" },
      { key: "find-level", label: "Open slots by level", tab: "Find", sub: "Level" },
    ],
  },
  {
    items: [
      { key: "stats", label: "Stats", tab: "Stats" },
      { key: "issues", label: "Issues", tab: "Issues", badge: "issues" },
      { key: "email", label: "Email", tab: "Email" },
      { key: "approvals", label: "Approvals", tab: "Approvals", badge: "approvals" },
    ],
  },
  // Admin-only. Filtered out of the rail entirely for staff accounts — the
  // server refuses these endpoints to them regardless, so this is signposting
  // rather than the enforcement.
  {
    label: "Admin",
    adminOnly: true,
    items: [
      { key: "accounts", label: "Accounts", tab: "Accounts", badge: "accounts" },
      { key: "activity", label: "Activity", tab: "Activity" },
    ],
  },
];

function NavRail({ activeTab, scheduleView, findView, onNavigate, pendingSignups, pendingAccounts, actionableIssueCount, hasIssueErrors, pendingApprovals, onShowTutorial, isAdmin, currentUser, onChangePassword, onLogout }) {
  function isActive(item) {
    if (item.tab !== activeTab) return false;
    if (!item.sub) return true;
    return item.sub === (item.tab === "Schedule" ? scheduleView : findView);
  }

  function badgeFor(item) {
    if (item.badge === "accounts" && pendingAccounts > 0) {
      return { count: pendingAccounts, className: "bg-indigo-500", title: `${pendingAccounts} account request${pendingAccounts !== 1 ? "s" : ""} awaiting review` };
    }
    if (item.badge === "signups" && pendingSignups > 0) {
      return { count: pendingSignups, className: "bg-blue-500", title: `${pendingSignups} sign-up${pendingSignups !== 1 ? "s" : ""} awaiting review` };
    }
    if (item.badge === "issues" && actionableIssueCount > 0) {
      return {
        count: actionableIssueCount,
        className: hasIssueErrors ? "bg-red-500" : "bg-amber-500",
        title: `${actionableIssueCount} issue${actionableIssueCount !== 1 ? "s" : ""} to review`,
      };
    }
    if (item.badge === "approvals" && pendingApprovals > 0) {
      return {
        count: pendingApprovals,
        className: "bg-amber-500",
        title: isAdmin
          ? `${pendingApprovals} change${pendingApprovals !== 1 ? "s" : ""} waiting for your approval`
          : `${pendingApprovals} change${pendingApprovals !== 1 ? "s" : ""} waiting for an admin`,
      };
    }
    return null;
  }

  return (
    <nav className="flex flex-col h-full py-3 px-2 gap-0.5 overflow-y-auto">
      {NAV.filter(section => !section.adminOnly || isAdmin).map((section, i) => (
        <div key={section.label || `flat-${i}`} className={i > 0 ? "mt-3" : ""}>
          {section.label ? (
            <div className="px-3 pb-1 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
              {section.label}
            </div>
          ) : (
            <div className="mx-3 mb-2 border-t border-gray-200" />
          )}
          {section.items.map(item => {
            const active = isActive(item);
            const badge = badgeFor(item);
            return (
              <button
                key={item.key}
                onClick={() => onNavigate(item)}
                aria-current={active ? "page" : undefined}
                className={`w-full flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium text-left transition-colors ${
                  active ? "bg-brand-blue text-white shadow-sm" : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                }`}
              >
                {item.icon}
                <span className="truncate">{item.label}</span>
                {badge && (
                  <span
                    className={`ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[11px] font-bold text-white ${
                      active ? "bg-white/25" : badge.className
                    }`}
                    title={badge.title}
                  >
                    {badge.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      ))}

      <div className="mt-auto pt-3">
        <div className="mx-3 mb-2 border-t border-gray-200" />
        <a
          href="/?schedule"
          target="_blank"
          rel="noopener noreferrer"
          className="w-full flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors"
          title="Open the member schedule lookup page in a new tab"
        >
          Member schedule <span aria-hidden="true" className="text-xs">↗</span>
        </a>
        <button
          onClick={onShowTutorial}
          className="w-full flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors text-left"
        >
          Tutorial
        </button>

        {/* Who you are, and the two things you can do about it. Last in the
            rail because it's the least-used corner of the app — but it's the
            one place both are, rather than split between here and the header. */}
        <div className="mx-3 my-2 border-t border-gray-200" />
        <div className="px-3 pb-1">
          <div className="text-sm font-medium text-gray-700 truncate" title={currentUser?.email}>
            {currentUser?.name || currentUser?.email}
          </div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className="text-xs text-gray-400 truncate">{currentUser?.email}</span>
            {currentUser?.role === "admin" && (
              <span className="text-[10px] bg-blue-100 text-blue-700 rounded px-1 py-0.5 flex-shrink-0">admin</span>
            )}
          </div>
        </div>
        <button
          onClick={onChangePassword}
          className="w-full flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors text-left"
        >
          Change password
        </button>
        <button
          onClick={onLogout}
          className="w-full flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors text-left"
        >
          Sign out
        </button>
      </div>
    </nav>
  );
}

function LoadScreen({ onLoad }) {
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  function parseFile(file) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        if (!Array.isArray(data.players) || !Array.isArray(data.groups)) {
          setError("Invalid file: expected { players: [], groups: [] }");
          return;
        }
        setError(null);
        onLoad(data);
      } catch {
        setError("Could not parse file — make sure it's valid JSON.");
      }
    };
    reader.readAsText(file);
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-8">
      <div className="max-w-md w-full">
        <div className="flex items-center gap-3 mb-1">
          <KacBadge />
          <h1 className="text-2xl font-display text-brand-blue">Kings Athletic Club</h1>
        </div>
        <p className="text-gray-500 text-sm mb-8">Load your roster data to get started.</p>
        <div
          onDrop={e => { e.preventDefault(); setDragOver(false); parseFile(e.dataTransfer.files[0]); }}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onClick={() => inputRef.current.click()}
          className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${
            dragOver ? "border-blue-400 bg-blue-50" : "border-gray-300 bg-white hover:border-blue-300 hover:bg-gray-50"
          }`}
        >
          <div className="text-4xl mb-3">📂</div>
          <p className="text-gray-700 font-medium mb-1">
            Drop <code className="bg-gray-100 px-1 rounded text-sm">kac_data.json</code> here
          </p>
          <p className="text-gray-400 text-sm">or click to browse</p>
          <input ref={inputRef} type="file" accept=".json,application/json" onChange={e => parseFile(e.target.files[0])} className="hidden" />
        </div>
        {error && (
          <p className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2">{error}</p>
        )}
      </div>
    </div>
  );
}

// Shown once a save has been refused or has failed. Saving stops at that
// point, so the only way on is a reload — which picks up whatever was saved
// elsewhere. Carrying on editing here would only pile up more lost changes.
function SaveProblemBanner({ problem }) {
  const message = problem === "conflict"
    ? "Someone else saved the schedule after you opened it, so your latest changes were not saved. Reload to get their version, then redo your changes."
    : "Your latest changes could not be saved. Reload the page before making any more.";
  return (
    <div role="alert" className="sticky top-0 z-50 bg-red-600 text-white px-4 py-3 flex flex-wrap items-center justify-between gap-3">
      <span className="text-sm font-medium">{message}</span>
      <button
        onClick={() => window.location.reload()}
        className="bg-white text-red-700 text-sm font-semibold rounded px-3 py-1 hover:bg-red-50"
      >
        Reload
      </button>
    </div>
  );
}

function AppShell({ currentUser, onLogout }) {
  const { players, groups, coaches, terms, activeTermName, movePlayer, addToGroup, updatePlayer, waitlistAdd, waitlistRemove, waitlistMove, updateGroup, removeFromGroup, createGroup, deleteGroup, loadData, adoptFromServer, addPlayers, addPlayersAndSave, linkHouseholds, addCoach, updateCoach, deleteCoach, loading, saveProblem, saveTerm, startNewTerm, switchToTerm, deleteTerm, renameTerm, duplicateTerm } = useKACState();
  const [activeTab, setActiveTab] = useState("Schedule");
  const [scheduleView, setScheduleView] = useState("List");
  const [findView, setFindView] = useState("Time");
  const [toastMessages, setToastMessages] = useState([]);
  const [notification, setNotification] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [newGroupPrefill, setNewGroupPrefill] = useState(null);
  const [filterTier, setFilterTier] = useState("All");
  const [showTutorial, setShowTutorial] = useState(false);
  // First sign-in on a newly activated account opens the walkthrough by
  // itself — that's the one moment a new person is guaranteed to see it.
  // Keyed by account id, so activating a second person shows it to them and
  // not again to anyone who has already dismissed it.
  useEffect(() => {
    if (!currentUser?.id) return;
    const key = `kac-tutorial-seen-${currentUser.id}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, "1");
      setShowTutorial(true);
    } catch {
      // Private browsing with storage blocked: skip the auto-open rather than
      // showing it on every single load.
    }
  }, [currentUser?.id]);
  const [showTerms, setShowTerms] = useState(false);
  // Rail is always on from lg up; below that it opens as an overlay drawer.
  const [railOpen, setRailOpen] = useState(false);
  // Badge count for the Sign-ups tab. Fetched once on load and refreshed each
  // time the tab is opened, so it reflects submissions made since sign-in.
  const [pendingSignups, setPendingSignups] = useState(0);
  const [pendingAccounts, setPendingAccounts] = useState(0);
  const [changingPassword, setChangingPassword] = useState(false);
  // Group id to re-examine for a free seat once state has settled.
  const [pendingSpotCheck, setPendingSpotCheck] = useState(null);
  // An assignment waiting on the "second clinic or move?" answer.
  const [pendingPlacement, setPendingPlacement] = useState(null);
  const [focusGroupId, setFocusGroupId] = useState(null);
  const headerRef = useRef(null);

  // Live schedule-issue list drives both the Issues tab and its nav badge.
  const issues = useMemo(
    () => (players && groups ? getScheduleIssues(players, groups, coaches) : []),
    [players, groups, coaches]
  );
  const actionableIssueCount = countActionableIssues(issues);

  // --- Approvals ---------------------------------------------------------
  // A staff account's placements and group changes never touch the schedule:
  // they're proposed here and an admin applies them. The pending list is held
  // by the app rather than each view, since it's drawn in several places at
  // once — the rail badge, the schedule, and the member card.
  const [approvals, setApprovals] = useState([]);
  // Bumped whenever a decision lands, so the Approvals tab reloads its own
  // copy (it lists decided requests too, which this one deliberately omits).
  const [approvalsVersion, setApprovalsVersion] = useState(0);

  const refreshApprovals = useCallback(() => {
    fetchApprovals()
      .then(setApprovals)
      .catch(() => {});
  }, []);

  useEffect(() => { refreshApprovals(); }, [refreshApprovals]);
  useEffect(() => {
    if (activeTab === "Approvals") refreshApprovals();
  }, [activeTab, refreshApprovals]);

  const overlay = useMemo(() => pendingOverlay(approvals), [approvals]);

  const refreshSignupCount = useCallback(() => {
    fetchApplications()
      .then(rows => setPendingSignups(rows.filter(a => (a.status || "pending") === "pending").length))
      .catch(() => {});
  }, []);

  useEffect(() => { refreshSignupCount(); }, [refreshSignupCount]);
  useEffect(() => {
    if (activeTab === "Sign-ups") refreshSignupCount();
  }, [activeTab, refreshSignupCount]);

  // Same idea for account requests, so a waiting request shows on the rail
  // without having to open the Accounts view. Staff accounts get a 403 here,
  // which is fine — the badge simply stays at zero for them.
  const isAdmin = currentUser?.role === "admin";
  useEffect(() => {
    if (!isAdmin) return;
    fetchAccounts()
      .then(rows => setPendingAccounts(rows.filter(a => a.status === "pending").length))
      .catch(() => {});
  }, [isAdmin, activeTab]);
  const hasIssueErrors = issues.some(i => i.severity === "error");

  // Jump from an issue row to the group it's about: switch to the Schedule
  // tab and open/focus that group (weekend sessions live on the Grid).
  // selectedPlayer only says whose card is open; the live record says what's
  // on it. Reading the snapshot meant every mutation needed a matching
  // hand-sync, and anything that forgot one (removing them from a group) left
  // the card showing stale information.
  const openPlayer = selectedPlayer
    ? players.find(p => p.id === selectedPlayer.id) || null
    : null;

  const jumpToGroup = useCallback((groupId) => {
    const g = groups.find(gr => gr.id === groupId);
    setActiveTab("Schedule");
    if (g && g.kind) { setScheduleView("Grid"); return; }
    setScheduleView("List");
    if (g) setFilterTier(g.tier);
    setFocusGroupId(groupId);
  }, [groups]);

  // Exposes the header's real (responsive) height as a CSS variable so
  // per-view sticky toolbars can stick right below it instead of
  // overlapping — avoids hardcoding a pixel offset that'd drift out of
  // sync if the header ever wraps to another line or its content changes.
  // Depends on [loading, players] because the header doesn't exist yet
  // during the initial "Loading schedule…"/load-screen render, so headerRef
  // isn't attached to anything until those flip — this effect needs to
  // re-run at that point instead of only on first mount.
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const update = () => document.documentElement.style.setProperty("--header-h", `${el.offsetHeight}px`);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading, players]);

  const navigate = useCallback((item) => {
    setActiveTab(item.tab);
    if (item.sub) {
      if (item.tab === "Schedule") setScheduleView(item.sub);
      else if (item.tab === "Find") setFindView(item.sub);
    }
    setRailOpen(false);
  }, []);

  // Staff propose; admins do. Everything gated funnels through here, so there
  // is one place that decides whether a change is made or asked for.
  const needsApproval = currentUser?.role !== "admin";

  const propose = useCallback(async (type, payload) => {
    const summary = summarizeRequest(type, payload, players || [], groups || []);
    try {
      const saved = await requestApproval({ type, payload, summary });
      setApprovals(prev => [...prev, saved]);
      setApprovalsVersion(v => v + 1);
      setNotification({ message: `Sent to an admin: ${summary}.` });
    } catch (e) {
      setNotification({ message: e.message || "Could not send that for approval." });
    }
  }, [players, groups]);

  const showWarnings = useCallback((messages) => setToastMessages(messages), []);
  const openPlayerModal = useCallback((player) => setSelectedPlayer(player), []);
  const openNewGroupModal = useCallback((prefill = {}) => setNewGroupPrefill(prefill), []);

  // Members sitting the term out aren't a job to be done, so they don't
  // swell the count that reads as a to-do.
  const unassignedCount = players ? countNeedingPlacement(players) : 0;

  // Memoised so the waitlist prompt below can depend on it without being
  // rebuilt on every render. No longer syncs selectedPlayer by hand — the
  // open card reads the live record (see openPlayer).
  //
  // Every hand-driven placement in the app funnels through here — the drag
  // targets on both schedule views, the group modal's search, the player
  // card's group list, Find, the waitlist prompt. Assigning someone who is
  // already in another clinic is ambiguous (a transfer? a second clinic?), so
  // instead of guessing, it stops and asks — offering to replace the clinic
  // they're in first, since that is nearly always what it turns out to be.
  // Creating a group with members keeps calling movePlayer directly: a
  // per-player prompt in the middle of a batch would be unusable.
  const handleMovePlayer = useCallback((playerId, fromGroupId, toGroupId) => {
    const player = players?.find(p => p.id === playerId);
    const target = toGroupId && groups?.find(g => g.id === toGroupId);
    const alreadyIn = player && target && !target.kind
      ? otherClinicsForPlayer(playerId, groups, toGroupId)
      : [];
    if (alreadyIn.length === 0) {
      if (needsApproval) propose("place", { playerId, fromGroupId: fromGroupId ?? null, toGroupId, mode: "move" });
      else movePlayer(playerId, fromGroupId, toGroupId);
      return;
    }
    setPendingPlacement({ playerId, fromGroupId, toGroupId });
  }, [players, groups, movePlayer, needsApproval, propose]);

  const resolvePlacement = useCallback((pending, mode) => {
    const { playerId, fromGroupId, toGroupId } = pending;
    const player = players.find(p => p.id === playerId);
    const name = player?.name || "Player";
    if (needsApproval) {
      const leaving = mode === "add" ? null : (fromGroupId ?? player?.groupId ?? null);
      propose("place", { playerId, fromGroupId: leaving, toGroupId, mode });
      setPendingPlacement(null);
      return;
    }
    if (mode === "add") {
      addToGroup(playerId, toGroupId);
      const kept = otherClinicsForPlayer(playerId, groups, toGroupId).map(g => g.id).join(", ");
      setNotification({ message: `${name} is now in ${toGroupId} as well — still in ${kept}.` });
    } else {
      const leaving = fromGroupId ?? player?.groupId ?? null;
      movePlayer(playerId, leaving, toGroupId);
      setNotification({
        message: leaving
          ? `Moved ${name} from ${leaving} to ${toGroupId}.`
          : `Moved ${name} to ${toGroupId}.`,
      });
      // The clinic they left may now have a seat for whoever is waiting.
      if (leaving) setPendingSpotCheck(leaving);
    }
    setPendingPlacement(null);
  }, [players, groups, movePlayer, addToGroup, needsApproval, propose]);

  // "Tell me, don't act": when a member leaves a group that has people
  // waiting, say so and offer to place the next in line. Nothing moves on its
  // own — the person at the top may no longer want the slot, or may not suit
  // the level, and only a human knows that.
  const offerWaitlistSpot = useCallback((groupId) => {
    const group = groups.find(g => g.id === groupId);
    if (!group || !hasSpace(group)) return;
    const nextId = getWaitlist(group)[0];
    const next = nextId && players.find(p => p.id === nextId);
    if (!next) return;
    setNotification({
      message: `${group.id} now has space — ${next.name} is first on the waitlist.`,
      actionLabel: `Place ${next.name.split(" ")[0]}`,
      onAct: () => handleMovePlayer(next.id, next.groupId, group.id),
      duration: 10000,
    });
  }, [groups, players, handleMovePlayer]);

  // Removal goes through here so the waitlist prompt can fire once the group
  // actually has a free seat. The check is deferred a tick: `groups` in this
  // closure is still the pre-removal snapshot.
  const handleRemoveFromGroup = useCallback((playerId, groupId) => {
    if (needsApproval) {
      propose("remove", { playerId, groupId });
      return;
    }
    removeFromGroup(playerId, groupId);
    setPendingSpotCheck(groupId);
  }, [removeFromGroup, needsApproval, propose]);

  useEffect(() => {
    if (!pendingSpotCheck) return;
    offerWaitlistSpot(pendingSpotCheck);
    setPendingSpotCheck(null);
  }, [pendingSpotCheck, offerWaitlistSpot]);

  function handleUpdatePlayer(playerId, updates) {
    updatePlayer(playerId, updates);
  }

  // The three group-level actions, gated the same way placements are.
  function handleCreateGroup(groupData) {
    if (needsApproval) { propose("create-group", { group: groupData }); return; }
    createGroup(groupData);
  }

  function handleUpdateGroup(groupId, updates) {
    if (needsApproval) { propose("update-group", { groupId, updates }); return; }
    updateGroup(groupId, updates);
  }

  function handleDeleteGroup(groupId) {
    if (needsApproval) { propose("delete-group", { groupId }); return; }
    deleteGroup(groupId);
  }

  // Approving applies the change through the same functions a hand edit uses,
  // then marks the request decided. The schedule saves on its usual delay.
  async function approveRequest(request) {
    const blocker = approvalBlocker(request, players, groups);
    if (blocker) {
      setNotification({ message: `Can't apply that: ${blocker}` });
      return;
    }
    applyApproval(request, { movePlayer, addToGroup, removeFromGroup, createGroup, updateGroup, deleteGroup });
    try {
      await decideApproval(request.id, "approve");
      setNotification({ message: `Approved: ${request.summary}` });
    } catch (e) {
      setNotification({ message: e.message || "The change was applied, but the request couldn't be marked approved." });
    }
    refreshApprovals();
    setApprovalsVersion(v => v + 1);
  }

  async function rejectRequest(request) {
    const reason = window.prompt(`Reject "${request.summary}"?\n\nA reason (optional) — whoever asked will see it:`, "");
    if (reason === null) return;
    try {
      await decideApproval(request.id, "reject", reason);
      setNotification({ message: `Rejected: ${request.summary}` });
    } catch (e) {
      setNotification({ message: e.message || "Could not reject that request." });
    }
    refreshApprovals();
    setApprovalsVersion(v => v + 1);
  }

  async function cancelRequest(request) {
    try {
      await decideApproval(request.id, "cancel");
      setNotification({ message: `Withdrawn: ${request.summary}` });
    } catch (e) {
      setNotification({ message: e.message || "Could not withdraw that request." });
    }
    refreshApprovals();
    setApprovalsVersion(v => v + 1);
  }

  function handleJoinGroup(groupId, playerIds) {
    const targetGroup = groups.find(g => g.id === groupId);
    if (!targetGroup) return;
    // A one-person join is the same gesture as a drag, so it gets the same
    // second-clinic question. Joining several at once doesn't: a prompt per
    // player mid-batch would be unusable, so those move and the toast names
    // whoever came out of another clinic.
    if (playerIds.length === 1) {
      const player = players.find(p => p.id === playerIds[0]);
      if (!player) return;
      const warnings = getWarnings(player, targetGroup);
      if (warnings.length > 0) showWarnings(warnings);
      handleMovePlayer(player.id, player.groupId, groupId);
      return;
    }

    const warnings = [];
    playerIds.forEach(playerId => {
      const player = players.find(p => p.id === playerId);
      if (!player) return;
      warnings.push(...getWarnings(player, targetGroup));
      if (needsApproval) {
        propose("place", { playerId, fromGroupId: player.groupId ?? null, toGroupId: groupId, mode: "move" });
        return;
      }
      const leaving = otherClinicsForPlayer(playerId, groups, groupId);
      if (leaving.length > 0) {
        warnings.push(`${player.name} was moved out of ${leaving.map(g => g.id).join(", ")}`);
      }
      movePlayer(playerId, player.groupId, groupId);
    });
    if (warnings.length > 0) showWarnings(warnings);
  }

  function handleCreateGroupWithPlayers(groupData, playerIds) {
    // Staff ask for the group and its members as one request: with nothing
    // created yet, there's no id for separate placements to point at.
    if (needsApproval) {
      propose("create-group", { group: { ...groupData, playerIds } });
      return;
    }
    // Predict the id createGroup() will assign so the new members can be
    // added in the same action.
    const maxNum = groups.reduce((max, g) => {
      const num = parseInt(g.id.replace("G", ""), 10);
      return isNaN(num) ? max : Math.max(max, num);
    }, 0);
    const predictedId = `G${String(maxNum + 1).padStart(3, "0")}`;

    createGroup(groupData);

    const warnings = [];
    const syntheticGroup = { ...groupData, playerIds: [] };
    playerIds.forEach(playerId => {
      const player = players.find(p => p.id === playerId);
      if (!player) return;
      warnings.push(...getWarnings(player, syntheticGroup));
      movePlayer(playerId, player.groupId, predictedId);
    });
    if (warnings.length > 0) showWarnings(warnings);
  }

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-gray-400 text-sm">Loading schedule…</div>
    </div>
  );

  // Loading a roster file writes the whole schedule, so it's an admin's to do
  // for the same reason terms are.
  if (!players) {
    if (!isAdmin) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-8">
          <p className="text-sm text-gray-500 text-center">
            There's no schedule loaded yet. An admin needs to load the roster before there's anything to work on.
          </p>
        </div>
      );
    }
    return <LoadScreen onLoad={loadData} />;
  }

  const placementPlayer = pendingPlacement
    ? players.find(p => p.id === pendingPlacement.playerId)
    : null;
  const placementTarget = pendingPlacement
    ? groups.find(g => g.id === pendingPlacement.toGroupId)
    : null;

  return (
    <div className="min-h-screen bg-gray-50">
      {saveProblem && <SaveProblemBanner problem={saveProblem} />}
      <header ref={headerRef} className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-40">
        <div className="px-4 py-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setRailOpen(o => !o)}
              className="lg:hidden text-gray-500 hover:text-gray-800 border border-gray-200 rounded px-2 py-1 transition-colors"
              aria-label="Toggle navigation menu"
              aria-expanded={railOpen}
            >
              <svg viewBox="0 0 20 20" fill="none" className="w-4 h-4" aria-hidden="true">
                <path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>
            <KacBadge size="w-8 h-8 text-lg" />
            <KacWordmark className="text-base sm:text-xl text-brand-blue" />
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-4">
            <span className="text-xs text-gray-400 hidden sm:inline">
              {players.length} players · {groups.length} groups
              {unassignedCount > 0 && (
                <span className="ml-2 text-amber-600 font-medium">· {unassignedCount} unassigned</span>
              )}
            </span>
            {/* Switching or rolling over a term rewrites every group at once,
                which would drive straight through the approvals queue — so it
                stays an admin's to do. Staff see which term they're in. */}
            {isAdmin ? (
              <button onClick={() => setShowTerms(true)} className="text-xs text-gray-500 hover:text-blue-600 border border-gray-200 rounded px-2 py-1 transition-colors flex items-center gap-1" title="Manage terms / seasons">
                <span aria-hidden="true">📅</span>
                <span className="font-medium">{activeTermName || "Terms"}</span>
              </button>
            ) : activeTermName ? (
              <span className="text-xs text-gray-500 border border-gray-200 rounded px-2 py-1 flex items-center gap-1" title="The term you're working in">
                <span aria-hidden="true">📅</span>
                <span className="font-medium">{activeTermName}</span>
              </span>
            ) : null}
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Side rail — permanent from lg up, overlay drawer below that. */}
        <div
          className={`fixed inset-0 z-40 bg-black/30 lg:hidden ${railOpen ? "" : "hidden"}`}
          onClick={() => setRailOpen(false)}
          aria-hidden="true"
        />
        {/* z-40, not z-30: the per-view sticky toolbars (GroupsView, Roster)
            are z-30 and live in <main>, which comes later in the DOM — at the
            same level they'd paint over the open drawer and swallow its
            clicks. Modals stay above at z-50. */}
        <aside
          className={`
            w-52 flex-shrink-0 bg-white border-r border-gray-200
            fixed lg:sticky left-0 z-40
            ${railOpen ? "flex" : "hidden lg:flex"}
          `}
          style={{ top: "var(--header-h, 0px)", height: "calc(100vh - var(--header-h, 0px))" }}
        >
          <NavRail
            activeTab={activeTab}
            scheduleView={scheduleView}
            findView={findView}
            onNavigate={navigate}
            pendingSignups={pendingSignups}
            pendingAccounts={pendingAccounts}
            isAdmin={isAdmin}
            actionableIssueCount={actionableIssueCount}
            hasIssueErrors={hasIssueErrors}
            pendingApprovals={approvals.length}
            onShowTutorial={() => { setShowTutorial(true); setRailOpen(false); }}
            currentUser={currentUser}
            onChangePassword={() => { setChangingPassword(true); setRailOpen(false); }}
            onLogout={onLogout}
          />
        </aside>

        <main className="flex-1 min-w-0">
        {activeTab === "Roster" && (
          <RosterView
            players={players}
            onPlayerClick={openPlayerModal}
            onUploadPlayers={addPlayers}
            onLinkHouseholds={linkHouseholds}
            onNotify={setNotification}
          />
        )}
        {activeTab === "Coaches" && (
          <CoachesView
            players={players}
            groups={groups}
            coaches={coaches}
            onAddCoach={addCoach}
            onUpdateCoach={updateCoach}
            onDeleteCoach={deleteCoach}
            onPlayerClick={openPlayerModal}
            movePlayer={handleMovePlayer}
            updateGroup={handleUpdateGroup}
            removeFromGroup={handleRemoveFromGroup}
            deleteGroup={handleDeleteGroup}
            onWarnings={showWarnings}
          />
        )}
        {activeTab === "Stats" && (
          <StatsView
            players={players}
            groups={groups}
            coaches={coaches}
          />
        )}
        {activeTab === "Sign-ups" && (
          <ApplicationsView
            players={players}
            addPlayers={addPlayersAndSave}
            onPendingChange={setPendingSignups}
            onNotify={setNotification}
          />
        )}
        {activeTab === "Email" && (
          <EmailView
            players={players}
            groups={groups}
            activeTermName={activeTermName}
            onNotify={setNotification}
          />
        )}
        {activeTab === "Approvals" && (
          <ApprovalsView
            currentUser={currentUser}
            players={players}
            groups={groups}
            onApprove={approveRequest}
            onReject={rejectRequest}
            onCancel={cancelRequest}
            reloadKey={approvalsVersion}
          />
        )}
        {activeTab === "Households" && (
          <HouseholdsView
            players={players}
            groups={groups}
            updatePlayer={updatePlayer}
            onPlayerClick={openPlayerModal}
            onNotify={setNotification}
          />
        )}
        {activeTab === "Accounts" && currentUser?.role === "admin" && (
          <AccountsView
            currentUser={currentUser}
            onNotify={setNotification}
            onPendingChange={setPendingAccounts}
          />
        )}
        {activeTab === "Activity" && currentUser?.role === "admin" && (
          <ActivityView onUndone={adoptFromServer} onNotify={setNotification} />
        )}
        {activeTab === "Issues" && (
          <IssuesView
            players={players}
            groups={groups}
            coaches={coaches}
            onJumpToGroup={jumpToGroup}
            onJumpToPlayer={id => {
              const p = players.find(pl => pl.id === id);
              if (p) openPlayerModal(p);
            }}
          />
        )}
        {activeTab === "Schedule" && scheduleView === "List" && (
          <GroupsView
            players={players}
            groups={groups}
            coaches={coaches}
            movePlayer={handleMovePlayer}
            updateGroup={handleUpdateGroup}
            removeFromGroup={handleRemoveFromGroup}
            deleteGroup={handleDeleteGroup}
            onWarnings={showWarnings}
            onPlayerClick={openPlayerModal}
            onCreateGroup={openNewGroupModal}
            overlay={overlay}
            currentUser={currentUser}
            onApproveRequest={approveRequest}
            onRejectRequest={rejectRequest}
            onCancelRequest={cancelRequest}
            filterTier={filterTier}
            onFilterTierChange={setFilterTier}
            onNotify={setNotification}
            onUpdatePlayerTier={(playerId, tier) => handleUpdatePlayer(playerId, { tier })}
            onWaitlistAdd={waitlistAdd}
            onWaitlistRemove={waitlistRemove}
            onWaitlistMove={waitlistMove}
            focusGroupId={focusGroupId}
            onFocusHandled={() => setFocusGroupId(null)}
          />
        )}
        {activeTab === "Schedule" && scheduleView === "Grid" && (
          <WeeklyGridView
            players={players}
            groups={groups}
            coaches={coaches}
            movePlayer={handleMovePlayer}
            updateGroup={handleUpdateGroup}
            removeFromGroup={handleRemoveFromGroup}
            deleteGroup={handleDeleteGroup}
            createGroup={handleCreateGroup}
            onWarnings={showWarnings}
            onPlayerClick={openPlayerModal}
            onCreateGroup={openNewGroupModal}
            onNotify={setNotification}
            onUpdatePlayerTier={(playerId, tier) => handleUpdatePlayer(playerId, { tier })}
            onWaitlistAdd={waitlistAdd}
            onWaitlistRemove={waitlistRemove}
            onWaitlistMove={waitlistMove}
          />
        )}
        {activeTab === "Find" && findView === "Time" && (
          <FindTimeView
            players={players}
            groups={groups}
            onPlayerClick={openPlayerModal}
            onJoinGroup={handleJoinGroup}
            onCreateGroupWithPlayers={handleCreateGroupWithPlayers}
          />
        )}
        {activeTab === "Find" && findView === "Player" && (
          <FindPlayerView
            players={players}
            groups={groups}
            onPlayerClick={openPlayerModal}
            onJoinGroup={handleJoinGroup}
            onNotify={setNotification}
          />
        )}
        {activeTab === "Find" && findView === "Level" && (
          <FindOpeningsView
            players={players}
            groups={groups}
            coaches={coaches}
            onPlayerClick={openPlayerModal}
            onJoinGroup={handleJoinGroup}
            onWaitlistAdd={waitlistAdd}
            onNotify={setNotification}
          />
        )}
        </main>
      </div>

      <Toast
        messages={toastMessages}
        onDismiss={() => setToastMessages([])}
        notification={notification}
        onDismissNotification={() => setNotification(null)}
      />

      {selectedPlayer && openPlayer && (
        <PlayerModal
          player={openPlayer}
          players={players}
          groups={groups}
          movePlayer={handleMovePlayer}
          updatePlayer={handleUpdatePlayer}
          onWarnings={showWarnings}
          onWaitlistAdd={waitlistAdd}
          onWaitlistRemove={waitlistRemove}
          onOpenGroup={jumpToGroup}
          onRemoveFromGroup={handleRemoveFromGroup}
          pendingRequests={overlay.byPlayer[openPlayer.id] || []}
          currentUser={currentUser}
          onApproveRequest={approveRequest}
          onRejectRequest={rejectRequest}
          onCancelRequest={cancelRequest}
          onClose={() => setSelectedPlayer(null)}
        />
      )}

      {newGroupPrefill !== null && (
        <NewGroupModal
          prefill={newGroupPrefill}
          groups={groups}
          onCreate={handleCreateGroup}
          onClose={() => setNewGroupPrefill(null)}
        />
      )}

      {showTutorial && (
        <TutorialModal onClose={() => setShowTutorial(false)} isAdmin={isAdmin} />
      )}

      {changingPassword && (
        <ChangePasswordModal
          onClose={() => setChangingPassword(false)}
          onNotify={setNotification}
        />
      )}

      {showTerms && (
        <TermsModal
          activeTermName={activeTermName}
          terms={terms}
          players={players}
          groups={groups}
          coaches={coaches}
          onSaveTerm={saveTerm}
          onStartNewTerm={startNewTerm}
          onSwitchTerm={switchToTerm}
          onDeleteTerm={deleteTerm}
          onRenameTerm={renameTerm}
          onDuplicateTerm={duplicateTerm}
          onNotify={setNotification}
          onClose={() => setShowTerms(false)}
        />
      )}

      {pendingPlacement && placementPlayer && placementTarget && (
        <SecondClinicModal
          player={placementPlayer}
          targetGroup={placementTarget}
          currentClinics={otherClinicsForPlayer(pendingPlacement.playerId, groups, pendingPlacement.toGroupId)}
          leavingGroup={groups.find(g => g.id === (pendingPlacement.fromGroupId ?? placementPlayer.groupId)) || null}
          onAddSecond={() => resolvePlacement(pendingPlacement, "add")}
          onMove={() => resolvePlacement(pendingPlacement, "move")}
          onClose={() => setPendingPlacement(null)}
        />
      )}

    </div>
  );
}

export default function App() {
  // null = still asking the server who we are; false = signed out.
  const [user, setUser] = useState(undefined);
  const [expiredNotice, setExpiredNotice] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const isPublicPage = params.has("schedule") || params.has("join");

  // Resolve the stored token on load. A token that expired while the tab was
  // closed lands us on the sign-in screen rather than in a half-working app.
  useEffect(() => {
    if (isPublicPage) return;
    let cancelled = false;
    fetchSession().then(u => {
      if (!cancelled) setUser(u || false);
    });
    return () => { cancelled = true; };
  }, [isPublicPage]);

  // A session can still lapse — after a long idle stretch, or because an
  // admin suspended the account. Whichever request first gets a 401 fires
  // this, so the app doesn't sit there looking signed in while every save
  // silently fails.
  useEffect(() => {
    return onSessionExpired(() => {
      setUser(false);
      setExpiredNotice(true);
    });
  }, []);

  async function handleLogout() {
    await logout();
    setUser(false);
    setExpiredNotice(false);
  }

  // Public member-facing schedule lookup at "/?schedule" — no staff login.
  // Checked before the auth gate so members never hit the sign-in screen.
  if (params.has("schedule")) {
    return <MemberScheduleView />;
  }

  // Public new-member sign-up form at "/?join" — also outside the auth gate.
  if (params.has("join")) {
    return <JoinView />;
  }

  if (user === undefined) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-sm text-gray-500">Loading…</p>
      </div>
    );
  }

  if (!user) {
    return (
      <>
        {expiredNotice && (
          <div className="fixed top-0 inset-x-0 z-50 bg-amber-500 text-white text-sm text-center py-2 px-4">
            You were signed out. Please sign in again.
          </div>
        )}
        <LoginScreen
          onLogin={u => {
            setExpiredNotice(false);
            setUser(u);
          }}
        />
      </>
    );
  }

  return <AppShell currentUser={user} onLogout={handleLogout} />;
}
